/**
 * Advanced Personaliser with Step-based and Single Form layouts
 * Supports label_internal for translation workflows
 */

function ensureTurnstileWidget() {
  if (!window.turnstile) throw new Error('Turnstile not loaded');

  if (window.__engravingTurnstileWidgetId) {
    return window.__engravingTurnstileWidgetId;
  }

  const container = document.getElementById('engraving-turnstile');
  if (!container) throw new Error('Turnstile container missing');

  const sitekey = window.ENGRAVING_TURNSTILE_SITEKEY;
  if (!sitekey) throw new Error('Turnstile site key missing');

  window.__engravingTurnstileWidgetId = turnstile.render(container, {
    sitekey,
    size: 'invisible',
    callback: (token) => {
      window.__engravingTurnstileLastToken = token;
    }
  });

  return window.__engravingTurnstileWidgetId;
}

if (!customElements.get('advanced-personaliser-modal')) {
  customElements.define(
    'advanced-personaliser-modal',
    class AdvancedPersonaliserModal extends ModalDialog {
      constructor() {
        super();
        
        this.config = window.advancedPersonaliserConfig || null;
        this.t = window.advancedPersonaliserTranslations || {};
        this.basePrice = window.advancedPersonaliserBasePrice || 0;
        this.selections = {};
        this.priceAdditions = 0;
        this.fieldsRendered = false;
        this.fontData = [];
        
        // Layout mode
        this.layoutMode = this.config?.layout_mode || 'single_form';
        this.steps = this.config?.steps || [];
        this.visitedSteps = new Set([0]);
        this.currentStepIndex = 0;  
        this.summaryExpanded = true;
        
        // Validate steps config
        if (this.layoutMode === 'steps' && (!this.steps.length || !this.validateStepsConfig())) {
          this.layoutMode = 'single_form';
        }
        
        // DOM references
        this.fieldsContainer = this.querySelector('[data-role="fields-container"]');
        this.basePriceEl = this.querySelector('[data-role="base-price"]');
        this.additionsPriceEl = this.querySelector('[data-role="additions-price"]');
        this.additionsRowEl = this.querySelector('[data-role="additions-row"]');
        this.totalPriceEl = this.querySelector('[data-role="total-price"]');
        this.dataInput = this.querySelector('[data-role="personalisation-data"]');
        this.addButton = this.querySelector('[data-role="personaliser-add"]');
        this.progressBar = this.querySelector('[data-role="step-progress"]');

        
        const observer = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (mutation.attributeName === 'open' && this.hasAttribute('open')) {
              this.onModalOpen();
            }
          });
        });
        observer.observe(this, { attributes: true });
        
        document.addEventListener('variant:changed', this.onVariantChange.bind(this));
        
        if (this.addButton) {
          this.addButton.addEventListener('click', this.handleSubmit.bind(this));
        }
      }
      
      validateStepsConfig() {
        if (!this.config?.fields) return false;
        
        for (const step of this.steps) {
          if (!step.id || !step.label || !step.sections?.length) return false;
        }
        
        return true;
      }
      
      onModalOpen() {
        if (this.addButton) {
          this.addButton.disabled = false;
          this.addButton.textContent = this.t.addToCart || 'Add to Cart';
        }
        
        this.updateBasePrice();
        
        if (this.config && this.fieldsContainer) {
          if (!this.fieldsRendered) {
            this.preloadFonts().then(() => {
              this.applyDefaultSelections();
              
              if (this.layoutMode === 'steps') {
                this.renderSteppedLayout();
              } else {
                this.renderSingleFormLayout();
              }
              
              this.renderPreviewPanel();
              this.updateFieldVisibility();
              this.updatePricing();
              this.updatePreview();
              this.fieldsRendered = true;
            });
          } else {
            this.updateFieldVisibility();
            this.updatePricing();
            this.updatePreview();
            
            if (this.layoutMode === 'steps') {
              this.updateStepStates();
            }
          }
        }
      }
      
      applyDefaultSelections() {
        this.config.fields.forEach(field => {
          if (field.default_value && field.options) {
            const defaultOption = field.options.find(opt => opt.value === field.default_value);
            if (defaultOption) {
              this.selections[field.key] = {
                value: defaultOption.value,
                label: defaultOption.label,
                label_internal: defaultOption.label_internal || defaultOption.label,
                price: defaultOption.price || 0,
                variant_id: defaultOption.variant_id || null
              };
            }
          }
        });
      }
      
      onVariantChange(event) {
        if (event.detail && event.detail.variant) {
          this.basePrice = event.detail.variant.price / 100;
          this.updateBasePrice();
          this.updatePricing();
        }
      }
      
      updateBasePrice() {
        const selectedVariant = document.querySelector('input[type="radio"][name]:checked');
        if (selectedVariant && selectedVariant.dataset.price) {
          this.basePrice = parseFloat(selectedVariant.dataset.price);
        }
        if (this.basePriceEl) {
          this.basePriceEl.textContent = this.formatMoney(this.basePrice * 100);
        }
      }
      
      async preloadFonts() {
        const fontsToLoad = [];
        const baseUrl = window.advancedPersonaliserFontBaseUrl || '';
        
        if (this.config.fonts && this.config.fonts.length) {
          this.config.fonts.forEach(font => {
            const name = typeof font === 'string' ? font : font.name;
            fontsToLoad.push(name);
          });
        }
        
        if (this.config.fields) {
          this.config.fields.forEach(field => {
            if (field.default_font && !fontsToLoad.includes(field.default_font)) {
              fontsToLoad.push(field.default_font);
            }
          });
        }
        
        if (!fontsToLoad.length) return;
        
        this.fontData = fontsToLoad.map(font => {
          const fontNameLower = font.toLowerCase().replace(/\s+/g, '-');
          return {
            name: font,
            label: font,
            url: `${baseUrl}personaliser-${fontNameLower}.woff2`
          };
        });
        
        const fontPromises = this.fontData.map(font => {
          const fontFace = new FontFace(font.name, `url(${font.url})`);
          return fontFace.load()
            .then(() => document.fonts.add(fontFace))
            .catch(err => console.warn(`Failed to load font: ${font.name}`, err));
        });
        
        return Promise.all(fontPromises);
      }
      
      // ========================================================================
      // STEPPED LAYOUT
      // ========================================================================
      
      renderSteppedLayout() {
        this.fieldsContainer.innerHTML = '';
        this.fieldsContainer.classList.add('personaliser-advanced__stepped');
        
        // Show progress bar
        if (this.progressBar) {
          this.progressBar.style.display = '';
          this.renderProgressBar();
        }

        
        // Create step container
        const stepContainer = document.createElement('div');
        stepContainer.className = 'personaliser-advanced__step-container';
        
        // Render each step
        this.steps.forEach((step, index) => {
          const stepEl = document.createElement('div');
          stepEl.className = 'personaliser-advanced__step';
          stepEl.dataset.stepId = step.id;
          stepEl.dataset.stepIndex = index;
          
          if (index !== 0) {
            stepEl.classList.add('is-hidden');
          }
          
          // Get fields for this step's sections
          const stepFields = this.getFieldsForStep(step);
          
          stepFields.forEach(field => {
            const fieldEl = this.createField(field);
            if (fieldEl) stepEl.appendChild(fieldEl);
          });
          
          // Add Back link if not first step
          if (index > 0) {
            const backLink = document.createElement('button');
            backLink.type = 'button';
            backLink.className = 'personaliser-advanced__step-back';
            backLink.innerHTML = `
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              ${this.t.previous || 'Back'}
            `;
            backLink.addEventListener('click', () => this.goToStep(index - 1));
            stepEl.insertBefore(backLink, stepEl.firstChild);
          }

          // Add Next button inside the step (not on last step)
          if (index < this.steps.length - 1) {
            const nextWrapper = document.createElement('div');
            nextWrapper.className = 'personaliser-advanced__step-next-wrapper';
            
            const nextButton = document.createElement('button');
            nextButton.type = 'button';
            nextButton.className = 'button button--primary personaliser-advanced__step-next';
            nextButton.textContent = this.t.next || 'Continue';
            nextButton.addEventListener('click', () => this.handleNextStep());
            
            nextWrapper.appendChild(nextButton);
            stepEl.appendChild(nextWrapper);
          }
          
          stepContainer.appendChild(stepEl);
        });
        
        this.fieldsContainer.appendChild(stepContainer);
        
        // Update step navigation
        this.updateStepNavigation();
        this.updateStepStates();
      }
      
      renderProgressBar() {
        if (!this.progressBar) return;
        
        this.progressBar.innerHTML = '';
        this.progressBar.classList.add('personaliser-advanced__progress');
        
        this.steps.forEach((step, index) => {
          const stepItem = document.createElement('button');
          stepItem.type = 'button';
          stepItem.className = 'personaliser-advanced__progress-item';
          stepItem.dataset.stepIndex = index;
          
          if (index === 0) {
            stepItem.classList.add('is-current');
          }
          
          stepItem.innerHTML = `
            <span class="personaliser-advanced__progress-indicator">
              <span class="personaliser-advanced__progress-number">${index + 1}</span>
              <svg class="personaliser-advanced__progress-tick" viewBox="0 0 16 16" fill="none">
                <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="personaliser-advanced__progress-label">${step.label}</span>
          `;
          
          stepItem.addEventListener('click', () => this.handleProgressClick(index));
          
          this.progressBar.appendChild(stepItem);
          
          // Add connector line between steps (except last)
          if (index < this.steps.length - 1) {
            const connector = document.createElement('div');
            connector.className = 'personaliser-advanced__progress-connector';
            this.progressBar.appendChild(connector);
          }
        });
      }
      
      getFieldsForStep(step) {
        return this.config.fields.filter(field => {
          return step.sections.includes(field.section);
        });
      }

      areAllRequiredFieldsComplete() {
        if (!this.config.fields) return true;
        
        return this.config.fields.every(field => {
          // Skip if field isn't visible
          if (!this.isFieldVisible(field)) return true;
          
          // Skip if not required
          if (!field.required) return true;
          
          const sel = this.selections[field.key];
          
          switch (field.type) {
            case 'select':
            case 'variant_select':
              return sel && sel.value && sel.value !== '';
            case 'text':
              return typeof sel === 'string' && sel.trim().length > 0;
            case 'image':
              return sel && sel.url;
            case 'checkbox':
              return sel && sel.checked === true;
            default:
              return true;
          }
        });
      }

      getStepIndexForField(field) {
        for (let i = 0; i < this.steps.length; i++) {
          if (this.steps[i].sections.includes(field.section)) {
            return i;
          }
        }
        return -1;
      }
      
      handleProgressClick(targetIndex) {
        // Can always go back
        if (targetIndex < this.currentStepIndex) {
          this.goToStep(targetIndex);
          return;
        }
        
        // To go forward, check if all previous steps are complete
        if (targetIndex > this.currentStepIndex) {
          for (let i = 0; i < targetIndex; i++) {
            if (!this.isStepComplete(i)) {
              // Highlight missing fields in first incomplete step
              this.goToStep(i);
              this.highlightMissingFields(i);
              return;
            }
          }
          this.goToStep(targetIndex);
        }
      }
      
      handleNextStep() {
        if (this.currentStepIndex >= this.steps.length - 1) return;
        
        // Check if current step is complete
        if (!this.isStepComplete(this.currentStepIndex)) {
          this.highlightMissingFields(this.currentStepIndex);
          return;
        }
        
        this.goToStep(this.currentStepIndex + 1);
      }
      
      goToStep(index) {
        if (index < 0 || index >= this.steps.length) return;
        
        // Auto-collapse summary on step 3+ (index >= 2) - mobile only
        if (index >= 2 && window.innerWidth < 750) {
          this.summaryExpanded = false;
          this.updateSummaryExpandedState();
        }
        
        const currentStep = this.fieldsContainer.querySelector(`[data-step-index="${this.currentStepIndex}"]`);
        const targetStep = this.fieldsContainer.querySelector(`[data-step-index="${index}"]`);
        
        if (currentStep) {
          currentStep.classList.add('is-hidden');
          currentStep.classList.remove('is-entering-left', 'is-entering-right');
        }
        
        if (targetStep) {
          targetStep.classList.remove('is-hidden');
          targetStep.classList.add(index > this.currentStepIndex ? 'is-entering-right' : 'is-entering-left');
          
          // Remove animation class after animation completes
          setTimeout(() => {
            targetStep.classList.remove('is-entering-left', 'is-entering-right');
          }, 300);
        }
        
        this.currentStepIndex = index;
        this.visitedSteps.add(index);

        this.updateStepNavigation();
        this.updateStepStates();
        this.updateFieldVisibility();
        
        // Scroll step container to top
        const container = this.querySelector('.personaliser-advanced__step-container');
        if (container) container.scrollTop = 0;
      }
      
      updateStepNavigation() {
        if (!this.addButton) return;
        
        if (this.layoutMode === 'steps') {
          // Show add button if all required fields are complete OR on final step
          const allComplete = this.areAllRequiredFieldsComplete();
          const isLastStep = this.currentStepIndex === this.steps.length - 1;
          this.addButton.style.display = (allComplete || isLastStep) ? '' : 'none';
          
          // Show ready message and pulse on final step
          const footer = this.querySelector('.personaliser-advanced__footer');
          if (footer) {
            let readyMessage = footer.querySelector('.personaliser-advanced__ready-message');
            
            if (isLastStep && allComplete) {
              if (!readyMessage) {
                readyMessage = document.createElement('div');
                readyMessage.className = 'personaliser-advanced__ready-message';
                readyMessage.innerHTML = `
                  <svg viewBox="0 0 16 16" fill="none">
                    <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  ${this.t.readyMessage || "You're all set!"}
                `;
                footer.insertBefore(readyMessage, footer.firstChild);
              }
              readyMessage.style.display = '';
              
              // Trigger pulse animation
              this.addButton.classList.remove('is-pulsing');
              void this.addButton.offsetWidth; // Force reflow
              this.addButton.classList.add('is-pulsing');
            } else if (readyMessage) {
              readyMessage.style.display = 'none';
              this.addButton.classList.remove('is-pulsing');
            }
          }
        }
      }
      
      updateStepStates() {
        if (!this.progressBar) return;
        
        const items = this.progressBar.querySelectorAll('.personaliser-advanced__progress-item');
        const connectors = this.progressBar.querySelectorAll('.personaliser-advanced__progress-connector');
        
        items.forEach((item, index) => {
          item.classList.remove('is-current', 'is-complete', 'is-incomplete');
          
          if (index === this.currentStepIndex) {
            item.classList.add('is-current');
          } else if (this.visitedSteps.has(index) && this.isStepComplete(index)) {
            item.classList.add('is-complete');
          } else {
            item.classList.add('is-incomplete');
          }
        });
        
        // Connectors show complete if the step BEFORE it is complete and visited
        connectors.forEach((connector, index) => {
          const stepComplete = this.visitedSteps.has(index) && this.isStepComplete(index);
          connector.classList.toggle('is-complete', stepComplete);
        });
      }
      
      isStepComplete(stepIndex) {
        const step = this.steps[stepIndex];
        if (!step) return false;
        
        const stepFields = this.getFieldsForStep(step);
        
        // If no fields in this step, it's complete
        if (stepFields.length === 0) return true;
        
        return stepFields.every(field => {
          // Skip if field isn't visible (due to show_if conditions)
          if (!this.isFieldVisible(field)) return true;
          
          // Skip if not required
          if (!field.required) return true;
          
          const sel = this.selections[field.key];
          
          switch (field.type) {
            case 'select':
            case 'variant_select':
              return sel && sel.value && sel.value !== '';
            case 'text':
              return typeof sel === 'string' && sel.trim().length > 0;
            case 'image':
              return sel && sel.url;
            case 'checkbox':
              return sel && sel.checked === true;
            default:
              return true;
          }
        });
      }
      
      highlightMissingFields(stepIndex) {
        const step = this.steps[stepIndex];
        if (!step) return;
        
        const stepEl = this.fieldsContainer.querySelector(`[data-step-index="${stepIndex}"]`);
        if (!stepEl) return;
        
        const stepFields = this.getFieldsForStep(step);
        
        stepFields.forEach(field => {
          if (!this.isFieldVisible(field) || !field.required) return;
          
          const fieldWrapper = stepEl.querySelector(`[data-field-key="${field.key}"]`);
          if (!fieldWrapper) return;
          
          const sel = this.selections[field.key];
          let isInvalid = false;
          
          switch (field.type) {
            case 'select':
            case 'variant_select':
              isInvalid = !sel || !sel.value;
              break;
            case 'text':
              isInvalid = !sel || typeof sel !== 'string' || !sel.trim();
              break;
            case 'image':
              isInvalid = !sel || !sel.url;
              break;
            case 'checkbox':
              isInvalid = !sel || !sel.checked;
              break;
          }
          
          fieldWrapper.classList.toggle('is-invalid', isInvalid);
          
          // Remove invalid class after user interacts
          if (isInvalid) {
            const input = fieldWrapper.querySelector('input, select, textarea');
            if (input) {
              const removeInvalid = () => {
                fieldWrapper.classList.remove('is-invalid');
                input.removeEventListener('change', removeInvalid);
                input.removeEventListener('input', removeInvalid);
              };
              input.addEventListener('change', removeInvalid);
              input.addEventListener('input', removeInvalid);
            }
          }
        });
      }
      
      // ========================================================================
      // SINGLE FORM LAYOUT
      // ========================================================================
      
      renderSingleFormLayout() {
        this.fieldsContainer.innerHTML = '';
        this.fieldsContainer.classList.add('personaliser-advanced__single-form');
        
        // Hide step navigation elements
        if (this.progressBar) this.progressBar.style.display = 'none';
        
        // Group fields by section
        const sectionMap = new Map();
        this.config.fields.forEach(field => {
          const sectionKey = field.section || 'default';
          if (!sectionMap.has(sectionKey)) {
            sectionMap.set(sectionKey, {
              key: sectionKey,
              label: field.section_label || field.label,
              fields: []
            });
          }
          sectionMap.get(sectionKey).fields.push(field);
        });
        
        const sections = Array.from(sectionMap.values());
        
        // Render each section
        sections.forEach(section => {
          const sectionEl = document.createElement('div');
          sectionEl.className = 'personaliser-advanced__form-section';
          sectionEl.dataset.section = section.key;
          
          if (sections.length > 1) {
            const header = document.createElement('h3');
            header.className = 'personaliser-advanced__form-section-title';
            header.textContent = section.label;
            sectionEl.appendChild(header);
          }
          
          section.fields.forEach(field => {
            const fieldEl = this.createField(field);
            if (fieldEl) sectionEl.appendChild(fieldEl);
          });
          
          this.fieldsContainer.appendChild(sectionEl);
        });
      }
      
      // ========================================================================
      // PREVIEW PANEL
      // ========================================================================
      
      renderPreviewPanel() {
        this.previewFields = this.config.fields.filter(f => f.type === 'text' && f.show_preview !== false);
        this.hasTextPreviews = this.previewFields.length > 0;
        
        const container = this.querySelector('[data-role="preview-panel"]');
        if (!container) return;
        
        let html = '<div class="personaliser-advanced__preview-panel-inner">';
        
        // Collapsible Summary section
        html += `
          <div class="personaliser-advanced__summary-wrapper">
            <button type="button" class="personaliser-advanced__summary-toggle" data-role="summary-toggle">
              <span class="personaliser-advanced__summary-toggle-text" data-role="summary-toggle-text">Your selections</span>
              <svg aria-hidden="true" focusable="false" class="personaliser-advanced__summary-toggle-icon icon icon-caret" viewBox="0 0 10 6">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/>
              </svg>
            </button>
            <div class="personaliser-advanced__summary" data-role="selection-summary"></div>
          </div>
        `;
        
        // Text previews
        if (this.hasTextPreviews) {
          html += '<div class="personaliser-advanced__previews">';
          
          this.previewFields.forEach(field => {
            const defaultFont = field.default_font || 'inherit';
            const defaultStyle = field.default_preview_style || 'silver';
            
            html += `
              <div class="personaliser-advanced__preview-item" data-preview-item="${field.key}" style="display: none;">
                <span class="personaliser-advanced__preview-label">${field.label}</span>
                <div class="personaliser-advanced__global-preview personaliser-advanced__field-preview--${defaultStyle}" data-preview-for="${field.key}">
                  <span class="preview-text" style="font-family: ${defaultFont}"></span>
                </div>
              </div>
            `;
          });
          
          html += '</div>';
          html += `<p class="personaliser-advanced__preview-caption">${this.t.previewCaption || 'Preview for illustration purposes only.'}</p>`;
        }
        
        html += '</div>';
        
        container.innerHTML = html;
      }
      
      // ========================================================================
      // FIELD VISIBILITY
      // ========================================================================
      
      isFieldVisible(field) {
        if (!field.show_if) return true;
        
        const condition = field.show_if;
        const parentField = this.config.fields.find(f => f.key === condition.field);
        
        if (parentField && !this.isFieldVisible(parentField)) return false;
        
    const selection = this.selections[condition.field];

// Handle unchecked checkbox - treat as value "no"
if (!selection) {
  if (parentField && parentField.type === 'checkbox') {
    return condition.value === 'no';
  }
  return false;
}
        
        const selectedValue = typeof selection === 'object' ? selection.value : selection;
        
        if (condition.values) {
          return condition.values.includes(selectedValue);
        }
        
        return selectedValue === condition.value;
      }
      
      updateFieldVisibility() {
        let newlyVisibleImageField = null;
        
        this.config.fields.forEach(field => {
          const wrapper = this.fieldsContainer.querySelector(`[data-field-key="${field.key}"]`);
          if (!wrapper) return;
          
          const wasVisible = wrapper.dataset.visible === 'true';
          const visible = this.isFieldVisible(field);
          wrapper.style.display = visible ? '' : 'none';
          wrapper.dataset.visible = visible ? 'true' : 'false';
          
          // Track newly visible image fields
          if (!wasVisible && visible && field.type === 'image') {
            newlyVisibleImageField = wrapper;
          }
          

        });
        
        // Scroll to newly visible image field
        if (newlyVisibleImageField) {
          setTimeout(() => {
            newlyVisibleImageField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
        
        this.updatePricing();
        this.updateDataInput();
        this.updatePreview();
        
        if (this.layoutMode === 'steps') {
          this.updateStepStates();
        }
      }
      
      // ========================================================================
      // FIELD CREATION
      // ========================================================================
      
      createField(field) {
        const wrapper = document.createElement('div');
        wrapper.className = `personaliser-advanced__field personaliser-advanced__field--${field.type}`;
        wrapper.dataset.fieldKey = field.key;
        
        if (field.show_if) {
          wrapper.style.display = 'none';
          wrapper.dataset.visible = 'false';
        }
        
        let created = true;
        switch (field.type) {
          case 'text': this.createTextField(field, wrapper); break;
          case 'select': 
            if (field.swatches) {
              this.createSwatchSelectField(field, wrapper);
            } else {
              this.createSelectField(field, wrapper);
            }
            break;
          case 'checkbox': this.createCheckboxField(field, wrapper); break;
          case 'image': this.createImageField(field, wrapper); break;
          case 'variant_select': this.createVariantSelectField(field, wrapper); break;
          default: created = false;
        }
        
        if (!created) return null;
        
        if (field.help_text && field.type !== 'text') {
          const helpText = document.createElement('p');
          helpText.className = 'personaliser-advanced__help-text';
          helpText.textContent = field.help_text;
          wrapper.appendChild(helpText);
        }
        
        return wrapper;
      }
      
      createCaretSvg() {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        svg.setAttribute('class', 'icon icon-caret');
        svg.setAttribute('viewBox', '0 0 10 6');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('fill-rule', 'evenodd');
        path.setAttribute('clip-rule', 'evenodd');
        path.setAttribute('d', 'M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z');
        path.setAttribute('fill', 'currentColor');
        svg.appendChild(path);
        return svg;
      }
      
      createTextField(field, wrapper) {
        const label = document.createElement('label');
        label.className = 'form__label';
        label.innerHTML = `
          <span>${field.label}</span>
          ${field.required ? `<span class="required">(${this.t.required || 'Required'})</span>` : ''}
          <span class="personaliser-advanced__char-count" data-char-count="${field.key}">0/${field.max_length || 100}</span>
          ${field.price > 0 ? `<span class="price-addition">+${this.formatMoney(field.price * 100)}</span>` : ''}
        `;
        
        const inputWrapper = document.createElement('div');
        inputWrapper.className = 'personaliser-advanced__input-wrapper';
        
        const textarea = document.createElement('textarea');
        textarea.className = 'field__input personaliser-advanced__textarea';
        textarea.placeholder = field.placeholder || '';
        textarea.maxLength = field.max_length || 100;
        if (field.required) textarea.required = true;
        
        textarea.addEventListener('input', (e) => {
          const maxLen = field.max_length || 100;
          
          // Enforce character limit (Android doesn't always respect maxLength)
          if (e.target.value.length > maxLen) {
            e.target.value = e.target.value.substring(0, maxLen);
          }
          
          this.selections[field.key] = e.target.value;
          this.updateCharCount(e.target, maxLen, field.key);
          this.updatePricing();
          this.updatePreview();
          this.updateDataInput();
          if (this.layoutMode === 'steps') this.updateStepStates();
          
          // Keep textarea visible after preview updates
          if (field.show_preview !== false) {
            setTimeout(() => {
              wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
          }
        });
        
        inputWrapper.appendChild(textarea);
        
        if (field.default_font) {
          textarea.style.fontFamily = field.default_font;
        }
        
        if (field.font_selector && this.fontData && this.fontData.length) {
          const fontSelect = document.createElement('select');
          fontSelect.className = 'select__select personaliser-advanced__font-select';
          fontSelect.innerHTML = `<option value="">${this.t.selectFont || 'Select a font'}</option>`;
          
          this.fontData.forEach(font => {
            const option = document.createElement('option');
            option.value = font.name;
            option.textContent = font.label || font.name;
            option.style.fontFamily = font.name;
            fontSelect.appendChild(option);
          });
          
          fontSelect.addEventListener('change', (e) => {
            this.selections[`${field.key}_font`] = e.target.value;
            textarea.style.fontFamily = e.target.value || 'inherit';
            this.updatePreview();
            this.updateDataInput();
          });
          
// Set default font in selections (for cart payload) but keep dropdown showing "Select a font"
          if (field.default_font) {
            this.selections[`${field.key}_font`] = field.default_font;
          }
          
          const selectWrapper = document.createElement('div');
          selectWrapper.className = 'select personaliser-advanced__select-wrapper';
          selectWrapper.appendChild(fontSelect);
          selectWrapper.appendChild(this.createCaretSvg());
          inputWrapper.appendChild(selectWrapper);
        }
        
        if (field.help_text) {
          const helpText = document.createElement('p');
          helpText.className = 'personaliser-advanced__help-text';
          helpText.textContent = field.help_text;
          inputWrapper.appendChild(helpText);
        }
        
        wrapper.appendChild(label);
        wrapper.appendChild(inputWrapper);
        
        return wrapper;
      }
      
      createSwatchSelectField(field, wrapper) {
        const label = document.createElement('label');
        label.className = 'form__label';
        label.innerHTML = `
          <span>${field.label}</span>
          ${field.required ? `<span class="required">(${this.t.required || 'Required'})</span>` : ''}
        `;
        
        const customSelect = document.createElement('div');
        customSelect.className = 'personaliser-advanced__swatch-select';
        
        // Selected display
        const selectedDisplay = document.createElement('button');
        selectedDisplay.type = 'button';
        selectedDisplay.className = 'personaliser-advanced__swatch-selected';
        
        // Dropdown
        const dropdown = document.createElement('div');
        dropdown.className = 'personaliser-advanced__swatch-dropdown';
        
        field.options.forEach(option => {
          const optionEl = document.createElement('button');
          optionEl.type = 'button';
          optionEl.className = 'personaliser-advanced__swatch-option';
          optionEl.dataset.value = option.value;
          optionEl.dataset.price = option.price || 0;
          optionEl.dataset.labelInternal = option.label_internal || option.label;
          
          const priceText = option.price > 0 ? ` (+${this.formatMoney(option.price * 100)})` : '';
          
          const swatchColor = this.getSwatchColor(option.value);
          optionEl.innerHTML = `
            <span class="personaliser-advanced__swatch-color" style="background-color: ${swatchColor};"></span>
            <span class="personaliser-advanced__swatch-label">${option.label}${priceText}</span>
          `;
          
          optionEl.addEventListener('click', () => {
            this.selections[field.key] = {
              value: option.value,
              label: option.label,
              label_internal: option.label_internal || option.label,
              price: option.price || 0
            };
            
            const swatchColor = this.getSwatchColor(option.value);
            selectedDisplay.innerHTML = `
              <span class="personaliser-advanced__swatch-color" style="background-color: ${swatchColor};"></span>
              <span class="personaliser-advanced__swatch-label">${option.label}${priceText}</span>
            `;
            selectedDisplay.appendChild(this.createCaretSvg());
            
            customSelect.classList.remove('is-open');
            
            // Update active state
            dropdown.querySelectorAll('.personaliser-advanced__swatch-option').forEach(opt => {
              opt.classList.toggle('is-selected', opt.dataset.value === option.value);
            });
            
            this.updateFieldVisibility();
            this.updatePricing();
            this.updatePreview();
            this.updateDataInput();
            if (this.layoutMode === 'steps') this.updateStepStates();
          });
          
          dropdown.appendChild(optionEl);
        });
        
        // Set default
        const defaultOption = field.default_value 
          ? field.options.find(o => o.value === field.default_value) 
          : field.options[0];
          
        if (defaultOption) {
          const priceText = defaultOption.price > 0 ? ` (+${this.formatMoney(defaultOption.price * 100)})` : '';
          const defaultSwatchColor = this.getSwatchColor(defaultOption.value);
          selectedDisplay.innerHTML = `
            <span class="personaliser-advanced__swatch-color" style="background-color: ${defaultSwatchColor};"></span>
            <span class="personaliser-advanced__swatch-label">${defaultOption.label}${priceText}</span>
          `;
          selectedDisplay.appendChild(this.createCaretSvg());
          
          this.selections[field.key] = {
            value: defaultOption.value,
            label: defaultOption.label,
            label_internal: defaultOption.label_internal || defaultOption.label,
            price: defaultOption.price || 0
          };
          
          dropdown.querySelector(`[data-value="${defaultOption.value}"]`)?.classList.add('is-selected');
        }
        
        // Toggle dropdown
        selectedDisplay.addEventListener('click', () => {
          customSelect.classList.toggle('is-open');
        });
        
        // Close on outside click
        document.addEventListener('click', (e) => {
          if (!customSelect.contains(e.target)) {
            customSelect.classList.remove('is-open');
          }
        });
        
        customSelect.appendChild(selectedDisplay);
        customSelect.appendChild(dropdown);
        
        wrapper.appendChild(label);
        wrapper.appendChild(customSelect);
        return wrapper;
      }

      createSelectField(field, wrapper) {
        const label = document.createElement('label');
        label.className = 'form__label';
        label.innerHTML = `
          <span>${field.label}</span>
          ${field.required ? `<span class="required">(${this.t.required || 'Required'})</span>` : ''}
        `;
        
        const selectWrapper = document.createElement('div');
        selectWrapper.className = 'select personaliser-advanced__select-wrapper';
        
        const select = document.createElement('select');
        select.className = 'select__select';
        if (field.required) select.required = true;
        
        if (!field.default_value) {
          const placeholder = field.placeholder || this.t.selectPlaceholder || 'Select an option';
          select.innerHTML = `<option value="">${placeholder}</option>`;
        }
        
        field.options.forEach(option => {
          const priceText = option.price > 0 ? ` (+${this.formatMoney(option.price * 100)})` : '';
          const selected = field.default_value === option.value ? ' selected' : '';
          const labelInternal = option.label_internal || option.label;
          select.innerHTML += `<option value="${option.value}" data-price="${option.price || 0}" data-label-internal="${this.escapeHtml(labelInternal)}"${selected}>${option.label}${priceText}</option>`;
        });
        
        select.addEventListener('change', (e) => {
          const selectedOption = e.target.options[e.target.selectedIndex];
          if (e.target.value) {
            this.selections[field.key] = {
              value: e.target.value,
              label: selectedOption.textContent.replace(/\s*\([^)]*\)\s*$/, '').trim(),
              label_internal: selectedOption.dataset.labelInternal || selectedOption.textContent.replace(/\s*\([^)]*\)\s*$/, '').trim(),
              price: parseFloat(selectedOption.dataset.price) || 0
            };
          } else {
            delete this.selections[field.key];
          }
          
          this.updateFieldVisibility();
          this.updatePricing();
          this.updatePreview();
          this.updateDataInput();
          if (this.layoutMode === 'steps') this.updateStepStates();
        });
        
        if (field.default_value) {
          const defaultOption = field.options.find(o => o.value === field.default_value);
          if (defaultOption) {
            this.selections[field.key] = {
              value: defaultOption.value,
              label: defaultOption.label,
              label_internal: defaultOption.label_internal || defaultOption.label,
              price: defaultOption.price || 0
            };
          }
        }
        
        selectWrapper.appendChild(select);
        selectWrapper.appendChild(this.createCaretSvg());
        
        wrapper.appendChild(label);
        wrapper.appendChild(selectWrapper);
        return wrapper;
      }
      
      createCheckboxField(field, wrapper) {
        const label = document.createElement('label');
        label.className = 'personaliser-advanced__checkbox-label';
        
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'personaliser-advanced__checkbox';
        
        checkbox.addEventListener('change', (e) => {
          this.selections[field.key] = {
            checked: e.target.checked,
            value: e.target.checked ? 'yes' : 'no',
            label: field.label,
            label_internal: field.label_internal || field.label,
            price: e.target.checked ? (field.price || 0) : 0
          };
          this.updateFieldVisibility();
          this.updatePricing();
          this.updateDataInput();
          if (this.layoutMode === 'steps') this.updateStepStates();
        });
        
        const priceText = field.price > 0 ? ` (+${this.formatMoney(field.price * 100)})` : '';
        const span = document.createElement('span');
        span.textContent = `${field.label}${priceText}`;
        
        label.appendChild(checkbox);
        label.appendChild(span);
        wrapper.appendChild(label);
        return wrapper;
      }
      
      createVariantSelectField(field, wrapper) {
  const label = document.createElement('label');
  label.className = 'form__label';
  label.innerHTML = `
    <span>${field.label}</span>
    ${field.required ? `<span class="required">(${this.t.required || 'Required'})</span>` : ''}
  `;
  
  const selectWrapper = document.createElement('div');
  selectWrapper.className = 'select personaliser-advanced__select-wrapper';
  
  const select = document.createElement('select');
  select.className = 'select__select';
  if (field.required) select.required = true;
  
  const placeholder = field.placeholder || this.t.selectPlaceholder || 'Select an option';
  select.innerHTML = `<option value="">${placeholder}</option>`;
  
  const stockData = window.advancedPersonaliserVariantStock || {};
  
  field.options.forEach(option => {
    const variantId = String(option.variant_id || '');
    const isOutOfStock = variantId && stockData[variantId] === false;
    
    const optionEl = document.createElement('option');
    optionEl.value = option.value;
    if (option.variant_id) optionEl.dataset.variantId = option.variant_id;
    optionEl.dataset.price = option.price || 0;
    optionEl.dataset.labelInternal = option.label_internal || option.label;
    
    const priceText = option.price > 0 ? ` (+${this.formatMoney(option.price * 100)})` : '';
    optionEl.textContent = option.label + priceText + (isOutOfStock ? ' - Out of Stock' : '');
    
    if (isOutOfStock) {
      optionEl.disabled = true;
    }
    
    select.appendChild(optionEl);
  });
  
  select.addEventListener('change', (e) => {
    const selectedOption = e.target.options[e.target.selectedIndex];
    if (e.target.value) {
      this.selections[field.key] = {
        value: e.target.value,
        label: selectedOption.textContent.replace(/\s*\([^)]*\)\s*$/, '').replace(' - Out of Stock', '').trim(),
        label_internal: selectedOption.dataset.labelInternal || selectedOption.textContent.replace(/\s*\([^)]*\)\s*$/, '').replace(' - Out of Stock', '').trim(),
        variant_id: selectedOption.dataset.variantId,
        price: parseFloat(selectedOption.dataset.price) || 0,
        zero_price: field.zero_price || false,
        type: 'variant_select'
      };
    } else {
      delete this.selections[field.key];
    }
    
    this.updateFieldVisibility();
    this.updatePricing();
    this.updateDataInput();
    if (this.layoutMode === 'steps') this.updateStepStates();
  });
  
  selectWrapper.appendChild(select);
  selectWrapper.appendChild(this.createCaretSvg());
  
  wrapper.appendChild(label);
  wrapper.appendChild(selectWrapper);
  return wrapper;
}
      
      createImageField(field, wrapper) {
        const label = document.createElement('label');
        label.className = 'form__label';
        label.innerHTML = `
          <span>${field.label}</span>
          ${field.price > 0 ? `<span class="price-addition">+${this.formatMoney(field.price * 100)}</span>` : ''}
        `;
        
        const uploadWrapper = document.createElement('div');
        uploadWrapper.className = 'personaliser-advanced__upload-wrapper';
        
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.className = 'personaliser-advanced__file-input';
        
        const uploadButton = document.createElement('button');
        uploadButton.type = 'button';
        uploadButton.className = 'button button--secondary';
        uploadButton.textContent = this.t.uploadImage || 'Upload image';
        
        const preview = document.createElement('div');
        preview.className = 'personaliser-advanced__image-preview';
        preview.style.display = 'none';
        
        const status = document.createElement('div');
        status.className = 'personaliser-advanced__upload-status';
        
        uploadButton.addEventListener('click', () => input.click());
        
        input.addEventListener('change', async (e) => {
          const file = e.target.files[0];
          if (!file) return;
          
          const maxSize = (field.max_size_mb || 5) * 1024 * 1024;
          if (file.size > maxSize) {
            status.textContent = `File too large. Max: ${field.max_size_mb || 5}MB`;
            status.className = 'personaliser-advanced__upload-status error';
            return;
          }
          
          status.textContent = this.t.uploading || 'Uploading...';
          status.className = 'personaliser-advanced__upload-status uploading';
          uploadButton.disabled = true;
          
          try {
            const formData = new FormData();
            formData.append('file', file);
            
            const widgetId = ensureTurnstileWidget();
            window.__engravingTurnstileLastToken = null;
            
            try {
              turnstile.execute(widgetId, { action: 'engraving_upload' });
            } catch (err) {
              console.warn('Turnstile execute failed, retrying...', err);
              turnstile.reset(widgetId);
              turnstile.execute(widgetId, { action: 'engraving_upload' });
            }
            
            await new Promise((resolve, reject) => {
              const start = Date.now();
              const timer = setInterval(() => {
                if (window.__engravingTurnstileLastToken) {
                  clearInterval(timer);
                  resolve();
                } else if (Date.now() - start > 8000) {
                  clearInterval(timer);
                  reject(new Error('Failed to obtain Turnstile token'));
                }
              }, 50);
            });
            
            const tsToken = window.__engravingTurnstileLastToken;
            formData.append('cf_turnstile_token', tsToken);
            
            const response = await fetch('https://workers.pulsetechuk.com', {
              method: 'POST',
              body: formData
            });
            
            if (!response.ok) throw new Error('Upload failed');
            
            turnstile.reset(widgetId);
            const data = await response.json();
            
            this.selections[field.key] = {
              url: data.url,
              filename: data.filename,
              key: data.key,
              price: field.price || 0,
              label_internal: field.label_internal || field.label
            };
            
            preview.innerHTML = `<img src="${data.url}" alt="Uploaded">`;
            preview.style.display = 'block';
            status.textContent = this.t.uploadComplete || 'Upload complete';
            status.className = 'personaliser-advanced__upload-status success';
            
            this.updatePricing();
            this.updateDataInput();
            if (this.layoutMode === 'steps') this.updateStepStates();
            
          } catch (err) {
            console.error('Upload error:', err);
            status.textContent = this.t.uploadFailed || 'Upload failed. Please try again.';
            status.className = 'personaliser-advanced__upload-status error';
            
            try {
              const widgetId = window.__engravingTurnstileWidgetId;
              if (widgetId) turnstile.reset(widgetId);
            } catch (e) {}
          }
          
          uploadButton.disabled = false;
        });
        
        uploadWrapper.appendChild(input);
        uploadWrapper.appendChild(uploadButton);
        uploadWrapper.appendChild(status);
        uploadWrapper.appendChild(preview);
        
        wrapper.appendChild(label);
        wrapper.appendChild(uploadWrapper);
        return wrapper;
      }
      
      // ========================================================================
      // PRICING & PREVIEW
      // ========================================================================
      
      updateCharCount(textarea, maxLength, fieldKey) {
        const charCount = this.querySelector(`[data-char-count="${fieldKey}"]`);
        if (charCount) charCount.textContent = `${textarea.value.length}/${maxLength || 100}`;
      }
      
      updatePricing() {
        this.priceAdditions = 0;
        
        if (this.config.fields) {
          this.config.fields.forEach(field => {
            if (!this.isFieldVisible(field)) return;
            
            const sel = this.selections[field.key];
            if (!sel) return;
            
            if (field.type === 'text' && field.price > 0 && typeof sel === 'string' && sel.trim()) {
              this.priceAdditions += field.price;
            } else if (field.type === 'select' && sel.price > 0) {
              this.priceAdditions += sel.price;
            } else if (field.type === 'checkbox' && sel.checked && sel.price > 0) {
              this.priceAdditions += sel.price;
            } else if (field.type === 'image' && sel.url && sel.price > 0) {
              this.priceAdditions += sel.price;
            } else if (field.type === 'variant_select' && sel.price > 0) {
              this.priceAdditions += sel.price;
            }
          });
        }
        
        if (this.additionsRowEl) {
          this.additionsRowEl.style.display = this.priceAdditions > 0 ? '' : 'none';
        }
        if (this.additionsPriceEl) {
          this.additionsPriceEl.textContent = this.formatMoney(this.priceAdditions * 100);
        }
        if (this.totalPriceEl) {
          this.totalPriceEl.textContent = this.formatMoney((this.basePrice + this.priceAdditions) * 100);
        }
      }
      
      updatePreview() {
        this.updateTextPreviews();
        this.updateSelectionSummary();
      }
      
      updateTextPreviews() {
        if (!this.previewFields || !this.previewFields.length) return;
        
        let anyPreviewVisible = false;
        
        this.previewFields.forEach(field => {
          const preview = this.querySelector(`[data-preview-for="${field.key}"]`);
          const previewItem = this.querySelector(`[data-preview-item="${field.key}"]`);
          if (!preview || !previewItem) return;
          
          const isFieldVisible = this.isFieldVisible(field);
          const text = this.selections[field.key];
          const hasText = text && typeof text === 'string' && text.trim().length > 0;
          
          // Only show preview on the step containing this field
          let isOnCurrentStep = true;
          if (this.layoutMode === 'steps') {
            const fieldStepIndex = this.getStepIndexForField(field);
            isOnCurrentStep = fieldStepIndex === this.currentStepIndex;
          }
          
          const shouldShow = isFieldVisible && hasText && isOnCurrentStep;
          previewItem.style.display = shouldShow ? '' : 'none';
          
          if (shouldShow) {
            anyPreviewVisible = true;
            const font = this.selections[`${field.key}_font`] || field.default_font || 'inherit';
            const fontSize = this.getPreviewFontSize(text);
            const previewStyle = this.getPreviewStyle(field);
            
            preview.className = 'personaliser-advanced__global-preview';
            if (previewStyle) {
              preview.classList.add(`personaliser-advanced__field-preview--${previewStyle}`);
            }
            
            preview.innerHTML = `<span class="preview-text" style="font-family: ${font}; font-size: ${fontSize}">${this.escapeHtml(text).replace(/\n/g, '<br>')}</span>`;
          }
        });
        
        // Hide caption if no previews visible
        const caption = this.querySelector('.personaliser-advanced__preview-caption');
        if (caption) {
          caption.style.display = anyPreviewVisible ? '' : 'none';
        }
      }
      
      updateSummaryExpandedState() {
        const wrapper = this.querySelector('.personaliser-advanced__summary-wrapper');
        if (!wrapper) return;
        
        wrapper.classList.toggle('is-collapsed', !this.summaryExpanded);
      }
      
      bindSummaryToggle() {
        const toggle = this.querySelector('[data-role="summary-toggle"]');
        if (!toggle || toggle.dataset.bound) return;
        
        toggle.dataset.bound = 'true';
        toggle.addEventListener('click', () => {
          this.summaryExpanded = !this.summaryExpanded;
          this.updateSummaryExpandedState();
        });
      }

      updateSelectionSummary() {
        const container = this.querySelector('[data-role="selection-summary"]');
        if (!container) return;
        
        const items = [];
        
        this.config.fields.forEach(field => {
          if (!this.isFieldVisible(field)) return;
          
          // For stepped layout, only show fields from visited+complete steps (not current)
          if (this.layoutMode === 'steps') {
            const fieldStepIndex = this.getStepIndexForField(field);
            if (fieldStepIndex === -1) return;
            // Don't show current step's fields in summary
            if (fieldStepIndex === this.currentStepIndex) return;
            // Only show if step was visited and is complete (has green tick)
            if (!this.visitedSteps.has(fieldStepIndex) || !this.isStepComplete(fieldStepIndex)) return;
          }
          
          const sel = this.selections[field.key];
          if (!sel) return;
          
          if (field.type === 'select' || field.type === 'variant_select') {
            if (sel.value && sel.value.toLowerCase() !== 'no') {
              items.push({
                label: this.titleCase(field.section_label || field.label.replace(/^(Choose|Select|Enter|Add) (a )?/i, '')),
                value: sel.label || sel.value,
                price: sel.price
              });
            }
} else if (field.type === 'text' && typeof sel === 'string' && sel.trim()) {
            items.push({
              label: this.titleCase(field.label.replace(/^(Choose|Select|Enter|Add) (a )?/i, '')),
              value: sel.length > 20 ? sel.substring(0, 20) + '...' : sel
            });
            // Also show font if one is selected for this text field
            const fontSelection = this.selections[`${field.key}_font`];
            if (fontSelection && field.font_selector) {
              items.push({
                label: 'Font',
                value: fontSelection
              });
            }
          } else if (field.type === 'checkbox' && sel.checked) {
            items.push({
              label: field.label,
              value: 'Yes',
              price: sel.price
            });
          } else if (field.type === 'image' && sel.url) {
            items.push({
              label: field.label,
              value: 'Image uploaded',
              price: sel.price
            });
          }
        });
        
        // Hide summary wrapper entirely on step 1
        const summaryWrapper = this.querySelector('.personaliser-advanced__summary-wrapper');
        if (summaryWrapper && this.layoutMode === 'steps') {
          summaryWrapper.style.display = this.currentStepIndex === 0 ? 'none' : '';
        }
        
        // Bind toggle after first render
        this.bindSummaryToggle();
        
        // Update toggle text with count
        const toggleText = this.querySelector('[data-role="summary-toggle-text"]');
        if (toggleText) {
          const selectionsText = this.t.yourSelections || 'Your selections';
          toggleText.textContent = items.length === 0 
            ? selectionsText 
            : `${selectionsText} (${items.length})`;
        }
        
        if (items.length === 0) {
          container.innerHTML = '';
          this.updateSummaryExpandedState();
          return;
        }
        
        container.innerHTML = items.map(item => `
          <div class="personaliser-advanced__summary-row">
            <span class="personaliser-advanced__summary-label">${item.label}</span>
            <span class="personaliser-advanced__summary-value">
              <span class="personaliser-advanced__summary-value-text">${item.value}</span>
              ${item.price ? `<span class="personaliser-advanced__summary-price">+${this.formatMoney(item.price * 100)}</span>` : ''}
            </span>
          </div>
        `).join('');
        
        this.updateSummaryExpandedState();
      }

      updateDataInput() {
        if (!this.dataInput) return;
        
        const cleanSelections = {};
        Object.keys(this.selections).forEach(key => {
          const baseKey = key.endsWith('_font') ? key.replace('_font', '') : key;
          const baseField = this.config.fields.find(f => f.key === baseKey);
          if (baseField && !this.isFieldVisible(baseField)) return;
          
          const value = this.selections[key];
          if (typeof value === 'object') {
            // Use label_internal as label in the JSON for ops
            const copy = { ...value };
            if (copy.label_internal) {
              copy.label = copy.label_internal;
              delete copy.label_internal;
            }
            cleanSelections[key] = copy;
          } else if (value && typeof value === 'string' && value.trim()) {
            cleanSelections[key] = value.trim();
          }
        });
        
        this.dataInput.value = JSON.stringify({
          selections: cleanSelections,
          price_additions: this.priceAdditions,
          config_handle: this.config.handle || 'unknown'
        });
        
        this.setHumanReadableProperties();
      }
      
      setHumanReadableProperties() {
        const form = document.querySelector('product-form form');
        if (!form) return;
        
        form.querySelectorAll('.personaliser-human-readable').forEach(el => el.remove());
        
        this.config.fields.forEach(field => {
          if (!this.isFieldVisible(field)) return;
          
          const value = this.selections[field.key];
          let displayValue = '';
          
          if (field.type === 'text' && value) {
            displayValue = value;
          } else if ((field.type === 'select' || field.type === 'variant_select') && value) {
            displayValue = value.label || value.value || '';
          } else if (field.type === 'checkbox' && value && value.checked) {
            displayValue = 'Yes';
          } else if (field.type === 'image' && value && value.url) {
            let imageLabel = field.label;
            if (field.show_if) {
              const parentField = this.config.fields.find(f => f.key === field.show_if.field);
              if (parentField) {
                imageLabel = parentField.label.replace('Choose ', '').replace('Select ', '');
              }
            }
            
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${imageLabel}]`;
            input.value = value.url;
            input.className = 'personaliser-human-readable';
            form.appendChild(input);
            return;
          }
          
          if (displayValue) {
            const isMethodSelect = field.type === 'select' &&
              this.config.fields.some(f => f.type === 'image' && f.show_if?.field === field.key);
            const cleanLabel = this.titleCase(field.label.replace(/^(Choose|Select|Enter|Add) (a )?/i, ''));
            const propertyName = isMethodSelect ? `_${cleanLabel}` : cleanLabel;
            
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${propertyName}]`;
            input.value = displayValue;
            input.className = 'personaliser-human-readable';
            form.appendChild(input);
          }
          
          if (field.font_selector && this.selections[`${field.key}_font`]) {
            const fontInput = document.createElement('input');
            fontInput.type = 'hidden';
            fontInput.name = `properties[${field.label} Font]`;
            fontInput.value = this.selections[`${field.key}_font`];
            fontInput.className = 'personaliser-human-readable';
            form.appendChild(fontInput);
          }
        });
      }
      
      // ========================================================================
      // SUBMIT HANDLING
      // ========================================================================
      
      getVariantAddons() {
        const addons = [];
        
        if (!this.config.fields) return addons;
        
        const form = document.getElementById(this.dataset.mainForm);
        let mainSku = this.dataset.variantSku || '';
        
        if (form && window.advancedPersonaliserVariantSkus) {
          const variantId = form.querySelector('[name="id"]')?.value;
          if (variantId && window.advancedPersonaliserVariantSkus[variantId]) {
            mainSku = window.advancedPersonaliserVariantSkus[variantId];
          }
        }
        
        this.config.fields.forEach(field => {
          if (field.type !== 'variant_select') return;
          if (!this.isFieldVisible(field)) return;
          
          const sel = this.selections[field.key];
          if (!sel || !sel.variant_id) return;
          
          addons.push({
            id: sel.variant_id,
            quantity: 1,
            properties: {
              '_zero_price_addon': sel.zero_price ? 'true' : 'false',
              '_addon_for': this.config.handle || 'personalisation',
              '_from_sku': mainSku,
              [field.label]: sel.label
            }
          });
        });
        
        return addons;
      }
      
      handleIncompleteUploads() {
        const imageFields = this.config.fields.filter(f => f.type === 'image');
        
        imageFields.forEach(imageField => {
          if (!imageField.show_if) return;
          
          const parentKey = imageField.show_if.field;
          const uploadTriggerValue = imageField.show_if.value;
          const parentSelection = this.selections[parentKey];
          
          if (parentSelection && parentSelection.value === uploadTriggerValue) {
            const imageValue = this.selections[imageField.key];
            const hasUpload = imageValue && imageValue.url;
            
            if (!hasUpload) {
              const parentField = this.config.fields.find(f => f.key === parentKey);
              if (parentField && parentField.options) {
                const emailLaterOption = parentField.options.find(opt =>
                  opt.value === 'email_later' ||
                  opt.value.includes('email')
                );
                
                if (emailLaterOption) {
                  this.selections[parentKey] = {
                    value: emailLaterOption.value,
                    label: emailLaterOption.label,
                    label_internal: emailLaterOption.label_internal || emailLaterOption.label,
                    price: emailLaterOption.price || 0
                  };
                }
              }
            }
          }
        });
      }
      
      async handleSubmit(e) {
  e.preventDefault();
  this.handleIncompleteUploads();
  
  const missingFields = [];
  
  this.config.fields.forEach(field => {
    if (!field.required || !this.isFieldVisible(field)) return;
    
    const sel = this.selections[field.key];
    
    if (field.type === 'text' && (!sel || !sel.trim())) {
      missingFields.push(field.label);
    } else if ((field.type === 'select' || field.type === 'variant_select') && (!sel || !sel.value)) {
      missingFields.push(field.label);
    } else if (field.type === 'image' && field.required && (!sel || !sel.url)) {
      missingFields.push(field.label);
    }
  });
  
  if (missingFields.length > 0) {
    alert(`Please complete: ${missingFields.join(', ')}`);
    return;
  }
  
  this.updateDataInput();
  
  if (this.addButton) {
    this.addButton.disabled = true;
    this.addButton.textContent = this.t.adding || 'Adding...';
  }
  
try {
    const bundleId = Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    const variantAddons = this.getVariantAddons();
    
    // Add bundle ID to all add-ons
    variantAddons.forEach(addon => {
      addon.properties['_bundle_id'] = bundleId;
    });
    
    if (variantAddons.length > 0) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: variantAddons })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        
        let failedLabel = '';
        for (const addon of variantAddons) {
          for (const key in this.selections) {
            const sel = this.selections[key];
            if (sel && String(sel.variant_id) === String(addon.id)) {
              failedLabel = sel.label;
              break;
            }
          }
          if (failedLabel) break;
        }
        
alert(failedLabel 
  ? (this.t.variantOutOfStock || 'Sorry, this option is now out of stock. Please select a different option.').replace('this option', `"${failedLabel}"`)
  : (this.t.variantOutOfStock || 'Sorry, this option is now out of stock. Please select a different option.')
);
        
        if (this.addButton) {
          this.addButton.disabled = false;
          this.addButton.textContent = this.t.addToCart || 'Add to Cart';
        }
        return;
      }
    }
    
    this.hide();

const form = document.getElementById(this.dataset.mainForm);
if (form) {
  // Add bundle ID to main product if we have add-ons
  if (variantAddons.length > 0) {
    let bundleInput = form.querySelector('input[name="properties[_bundle_id]"]');
    if (!bundleInput) {
      bundleInput = document.createElement('input');
      bundleInput.type = 'hidden';
      bundleInput.name = 'properties[_bundle_id]';
      form.appendChild(bundleInput);
    }
    bundleInput.value = bundleId;
  }
  form.requestSubmit();
}
    
  } catch (err) {
    console.error('Add to cart error:', err);
    alert(this.t.addToCartError || 'Failed to add to cart. Please try again.');
    
    if (this.addButton) {
      this.addButton.disabled = false;
      this.addButton.textContent = this.t.addToCart || 'Add to Cart';
    }
  }
}
      
      // ========================================================================
      // UTILITIES
      // ========================================================================
      
getCurrencyRate() {
        return Shopify.currency?.rate || 1;
      }
      
      formatMoney(cents) {
        return Shopify.formatMoney ? Shopify.formatMoney(cents) : `£${(cents / 100).toFixed(2)}`;
      }

      titleCase(str) {
        return str.replace(/\b\w/g, c => c.toUpperCase());
      }
      
      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }
      
      getPreviewFontSize(text) {
        const len = text.length;
        if (len <= 3) return '6rem';
        if (len <= 8) return '4rem';
        if (len <= 15) return '3.2rem';
        if (len <= 25) return '2.8rem';
        if (len <= 40) return '1.9rem';
        return '1.5rem';
      }
      
      getSwatchColor(value) {
        const normalised = value.toLowerCase().replace(/[\s-]+/g, '_');
        
        const swatchMap = {
          'sterling_silver': '#C0C0C0',
          'silver': '#C0C0C0',
          'white_gold': '#C0C0C0',
          '9ct_white_gold': '#C0C0C0',
          'gold': '#d4af37',
          '9ct_gold': '#d4af37',
          '9ct_yellow_gold': '#d4af37',
          'gold_plated': '#d4af37',
          'gold_plated_sterling_silver': '#d4af37',
          'rose_gold': '#e3a177',
          'rose_gold_plated': '#e3a177',
          'rose_gold_plated_sterling_silver': '#e3a177'
        };
        
        // Check for direct match
        if (swatchMap[normalised]) return swatchMap[normalised];
        
        // Check for partial matches
        if (normalised.includes('rose_gold') || normalised.includes('rose')) return '#e3a177';
        if (normalised.includes('gold')) return '#d4af37';
        if (normalised.includes('silver') || normalised.includes('white')) return '#C0C0C0';
        
        return null;
      }
      
      getPreviewStyle(field) {
        const metalMap = {
          'sterling_silver': 'silver',
          'silver': 'silver',
          'gold': 'gold',
          '9ct_yellow_gold': 'gold',
          'gold_plated': 'gold',
          'rose_gold': 'rose_gold',
          'rose_gold_plated': 'rose_gold',
          '9ct_white_gold':'silver',
          'gold_plated_sterling_silver':'gold',
          'rose_gold_plated_sterling_silver':'rose_gold'
        };
        
        if (field.preview_style_from) {
          const sourceSelection = this.selections[field.preview_style_from];
          if (sourceSelection && sourceSelection.value) {
            const mapped = metalMap[sourceSelection.value];
            if (mapped) return mapped;
          }
        }
        
        return field.default_preview_style || null;
      }
    }
  );
}